import React, { useState } from 'react'
import { ExperienceContext } from '../../../contexts/ExperienceContext';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useUserContext } from '../../../contexts/UserContext';
import { getAllTemplateData, getAllExperiencesData } from '../../../utils/APIs';
import ManageTemplates from '../../../components/Template/ManageTemplates';
import { deleteTemplateById, deleteExperienceById } from '../../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../../constants/analyticsEvents';
import ManageExperiences from '../../../components/Experience/ManageExperiences';
import CreateTemplate from '../../../components/Template/CreateTemplate';
import EditExperience from '../../../components/Experience/EditExperience';
import CreateExperience from '../../../components/Experience/CreateExperience';
import ManageOffers from '../../../components/Offers/ManageOffers';
import EditOffer from '../../../components/Offers/EditOffer';
import EditTemplate from '../../../components/Template/EditTemplate';
import ManageAEMContentFragments from '../../../components/ManageAEMContentFragments';
import AEMContentDetails from '../../../components/ManageAEMContentFragments/AEMContentDetails';
import ManageContentfulEntries from '../../../components/ManageContentfulEntries';
import ContentfulEntryDetails from '../../../components/ManageContentfulEntries/ContentfulEntryDetails';
import { permission } from '../../../config/permission';
import BulkPreview from '../../LeftSideNav/BulkPreview';
import ExperienceDetails from '../../../components/Experience/ExperienceDetails';
import BulkActivation from '../BulkActivation';
import TemplatesPreview from '../../../components/Template/TemplatesPreview';
import ManageTealiumExperiences from '../../../components/ManageTealiumEperiences';
import TealiumExperienceDetails from '../../../components/ManageTealiumEperiences/TealiumExperienceDetails';
import ManageGithubEntries from '../../../components/ManageGithubEntries/ManageGithubEntries';
import GithubEntryDetails from '../../../components/ManageGithubEntries/GithubEntryDetails';

const Experiences = ({ selectedOrgLevel }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    if (sessionStorage.getItem('selectedTemplate')) {
      return sessionStorage.getItem('selectedTemplate');
    }
    return '';
  });
  const { userData, checkPermission } = useUserContext();
  const [data, setData] = useState({});
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [experiencesData, setExperiencesData] = useState([]);
  const [isGlobalTemplates, setIsGlobalTemplates] = useState(false);

  const getAllExperiencesHandler = async () => {
    try {
      setLoading(true);
      const result = await getAllExperiencesData(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        userData?.defaultAdobeIO ? userData?.defaultAdobeIO._id : "",
        userData?.defaultContentful ? userData?.defaultContentful._id : "",
        userData?.defaultTealiumKey ? userData?.defaultTealiumKey?._id : "",
        userData?.defaultGithub ? userData?.defaultGithub?._id : "",
      );

      setExperiencesData(result.data);
    } catch (error) {
      console.log('error', error)
    } finally {
      setLoading(false);
    }
  };

  const deleteExperienceHandler = async (id, checkDependencies=false) => {
    try {
      const payload = {};
      if(typeof id === "string") payload.id = id;
      else payload.ids = id;
      setLoading(true);
      const deleteResult = await deleteExperienceById(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload,
        checkDependencies,
        !checkDependencies ? {
          event_name: ANALYTICS_EVENTS.DELETE_EXPERIENCE,
          metadata: { experience_ids: typeof id === 'string' ? [id] : id }
        } : null
      );
      if(checkDependencies) {
        return deleteResult
      }
      if (deleteResult.status) {
        await getAllExperiencesHandler()
      }
      return deleteResult;
    } catch (error) {
      console.log('error', error)
    } finally {
      setLoading(false);
    }
  }

  const getTemplateData = async () => {
    try {
      setLoading(true);
      const result = await getAllTemplateData(
        userData?.token,
        userData?.selectedOrganization?.org_token
      );

      const formattedData = {}
      result.data.forEach((item) => {
        if (item?.html && item?.css && item?.json)
          formattedData[item.name] = {
            name: item.name,
            code: {
              html: `function(template) { return \`${item?.html}\` }`,
              styles: item?.css,
            },
            base_config_json: JSON.parse(`${item?.json}`),
            experience_json: getExperienceJSON(JSON.parse(`${item?.json}`)),
          };
      });

      setData((prev) => ({ ...formattedData, ...prev }))
    } catch (error) {
      console.log('error', error)
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id, checkDependencies=false) => {
    try {
      const result = await deleteTemplateById(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        id,
        "",
        checkDependencies,
        !checkDependencies ? {
          event_name: ANALYTICS_EVENTS.DELETE_TEMPLATE,
          metadata: { template_id: id }
        } : null
      );
      if(checkDependencies) {
        return result
      }
      if ( !checkDependencies && result.status) {
        const result = await getAllTemplateData(
          userData?.token,
          userData?.selectedOrganization?.org_token,
          isGlobalTemplates ? {scope:'global'} : {}
        );

        setRawData(result.data);
      }
      return result;
    } catch (error) {
      console.log('error', error)
    }
  }

  const getTemplatesRawData = async (isGlobal=isGlobalTemplates) => {
    try {
      setLoading(true);
      const result = await getAllTemplateData(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        isGlobal ? {scope:'global'} : {}
      );

      setRawData(result.data);
    } catch (error) {
      console.log('error', error)
    } finally {
      setLoading(false);
    }
  };

  const getExperienceJSON = (base_config_json) => {
    const experience_json = {};
    base_config_json.forEach(function (item) {
      experience_json[item.id] = item.default_value;
    })
    return experience_json;
  }

  return (
    <div className="w-full">
      <ExperienceContext.Provider value={{ selectedTemplate, setSelectedTemplate, getAllExperiencesHandler, data, setData, getTemplateData, loading, setLoading, rawData, getTemplatesRawData, deleteTemplate, selectedTemplateId, setSelectedTemplateId, setRawData, experiencesData, setExperiencesData, deleteExperienceHandler, isGlobalTemplates, setIsGlobalTemplates }}>
        <Routes>
          {selectedOrgLevel?.route_permission["manage-templates"] == true && (
            <>
              {checkPermission(permission.TEMPLATE_CREATE) && <Route path="/create-template" element={<CreateTemplate />} />}
              <Route path="/edit-template/:id?" element={<EditTemplate />} />
              <Route path="/manage-templates" element={<ManageTemplates />} />
              {selectedOrgLevel?.route_permission["templates-preview"] && (
                <Route path="/templates-preview" element={<TemplatesPreview />} />
              )}
            </>
          )}

          {selectedOrgLevel?.route_permission["manage-experiences"] == true && (
            <>
              {
                selectedOrgLevel?.route_permission["create-experiences"] == true &&
                <Route path="/create-experience" element={<CreateExperience />} />
              }
              <Route path="/edit-experience/:id?" element={<EditExperience />} />
              <Route path="/manage-experiences" element={<ManageExperiences />} />
              <Route path="/bulk-preview" element={<BulkPreview />} />
              <Route path="/experience-details/:id" element={<ExperienceDetails />} />
              <Route path="/bulk-activation" element={<BulkActivation />} />
            </>
          )}

          {selectedOrgLevel?.route_permission["manage-offers"] == true && (
            <>
              <Route path="/manage-offers" element={<ManageOffers />} />
              <Route path="/edit-offer/:id?" element={<EditOffer />} />
            </>
          )}

          {selectedOrgLevel?.route_permission["aem-content-fragments"] == true && (
            <>
              <Route path="/aem-content-fragments" element={<ManageAEMContentFragments />} />
              <Route path="/aem-content-fragments/:id?" element={<AEMContentDetails />} />
            </>
          )}

          {selectedOrgLevel?.route_permission["contentful-entries"] == true && (
            <>
              <Route path="/contentful-entries" element={<ManageContentfulEntries />} />
              <Route path="/contentful-entries/:id?" element={<ContentfulEntryDetails />} />
            </>
          )}

          {selectedOrgLevel?.route_permission["tealium-experiences"] == true && (
            <>
              <Route path="/tealium-experiences" element={<ManageTealiumExperiences />} />
              <Route path="/tealium-experiences/:id?" element={<TealiumExperienceDetails />} />
            </>
          )}

          {selectedOrgLevel?.route_permission["github-entries"] == true && (
            <>
              <Route path="/github-entries" element={<ManageGithubEntries />} />
              <Route path="/github-entries/details/" element={<GithubEntryDetails />} />
            </>
          )}

          {/* <Route path="/code" element={<ExperienceCode />} />
          <Route path="/preview" element={<preview />} />
          <Route path="/choose-template" element={<ChooseTemplate />} />
          <Route path="/configure" element={<ConfigureExperience />} /> */}

          <Route path="*" element={<Navigate to="/dashboard" replace={true} />} />
        </Routes>
        {/* {path == 'choose-template' ? <ChooseTemplate />
          : path == 'configure' ? <ConfigureExperience />
          : path == 'create-experience' ? <CreateExperience />
            : path == 'edit-experience' ? <EditExperience />
              : path == 'code' ? <ExperienceCode />
                : path == 'preview' ? <preview />
                : path == 'manage-experiences' ? <ManageExperiences />
                  : path == 'create-template' ? <CreateTemplate />
                    : path == 'edit-template' ? <EditTemplate />
                      : path == 'manage-offers' ? <ManageOffers />
                        : path == 'edit-offer' ? <EditOffer />
                      : <ManageTemplates />
        } */}
      </ExperienceContext.Provider>
    </div>
  );
}

export default Experiences;